// ProgressModal.tsx — instead of a greyed-out button: what is happening, each step with its state, a bar, and the time
// so far. The bar is a REAL percentage when the engine reports one; otherwise it shows activity (moving stripes), never
// an invented number. Closing it does not stop the work — it keeps going, and the page shows the result.
import { useEffect, useState } from "react";
import { stepStates, elapsed, type Progress } from "./progress.ts";

const ICON = { done: "✓", running: "●", todo: "○", failed: "✗" } as const;
const COLOUR = { done: "#16a34a", running: "#2563eb", todo: "#9ca3af", failed: "#dc2626" } as const;

export function ProgressModal({ p, onClose }: { p: Progress | null; onClose: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!p || p.state !== "running") return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [p]);
  if (!p) return null;
  const states = stepStates(p);
  return (
    <div role="dialog" aria-modal="true" aria-label={p.title}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <style>{`@keyframes pm-stripes { from { background-position: 0 0 } to { background-position: 40px 0 } }`}</style>
      <div style={{ background: "#fff", borderRadius: 12, width: "min(460px, 92vw)", padding: 20, boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{p.title}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{elapsed((p.state === "running" ? now : now) - p.startedAt)}</div>
        </div>

        <div style={{ height: 8, borderRadius: 4, background: "#e5e7eb", overflow: "hidden", margin: "14px 0" }}>
          {p.pct != null || p.state !== "running"
            ? <div style={{ height: "100%", width: `${p.state === "error" ? 100 : (p.pct ?? 100)}%`, background: p.state === "error" ? "#dc2626" : p.state === "done" ? "#16a34a" : "#2563eb", transition: "width .4s" }} />
            : <div style={{ height: "100%", width: "100%", animation: "pm-stripes 0.8s linear infinite",
                background: "repeating-linear-gradient(45deg, #2563eb 0 10px, #60a5fa 10px 20px)", backgroundSize: "40px 100%" }} />}
        </div>

        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
          {p.steps.map((s, i) => (
            <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: states[i] === "todo" ? "#9ca3af" : "#111827" }}>
              <span style={{ color: COLOUR[states[i]!], width: 14 }}>{ICON[states[i]!]}</span><span>{s}</span>
            </li>))}
        </ol>

        {p.detail && <div style={{ marginTop: 12, fontSize: 13, color: "#374151", background: "#f9fafb", borderRadius: 6, padding: "8px 10px" }}>{p.detail}</div>}
        {p.error && <div style={{ marginTop: 12, fontSize: 13, color: "#b91c1c", background: "#fef2f2", borderRadius: 6, padding: "8px 10px" }}>{p.error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          {p.links.map((l) => <a key={l.href} href={l.href} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 6, background: "#111827", color: "#fff", textDecoration: "none" }}>{l.label}</a>)}
          <button onClick={onClose} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>
            {p.state === "running" ? "Keep working in the background" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
